import { ref, computed, watch } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import moment from 'moment-timezone'
import store from '@/store'
import router from '@/router'
import ability from '@/libs/acl/ability'
import { debounce } from 'lodash'
import useFilterCounter from '@/components/Filters/useFiltersCount'

export default function useList() {
  const refListTable = ref(null)

  const toast = useToast()

  store.dispatch('tenant/getAll')
  store.dispatch('unit/getAll', { with: 'router:id,id_tenant' })
  store.dispatch('center/getAll')
  store.dispatch('sector/getAll')
  store.dispatch('omniview_reseller/getAll')
  store.dispatch('hanwha_alarm_profiles/getAll')

  const tenantOptions = computed(() => store.getters['tenant/getSelectedItems'])
  const tenantFilter = ref(null)
  const unitFilter = ref(null)
  const alarmProfileFilter = ref(null)
  const facetedFilters = ref({})
  const unitOptions = computed(() => store.getters['unit/getSelectedItems'])
  const centerOptions = computed(() => store.getters['center/getSelectedItems'])
  const sectorOptions = computed(() => store.getters['sector/getSelectedItems'])
  const resellerOptions = computed(() => store.getters['omniview_reseller/getSelectedItems'])
  const dvrOptions = computed(() => store.getters['hanwha_dvr/getSelectedItems'])
  const alarmProfileOptions = computed(() => store.getters['hanwha_alarm_profiles/getSelectedItems'])

  if (router.currentRoute.params.id_tenant)
    tenantFilter.value = parseInt(router.currentRoute.id_tenant)

  const tableColumns = [
    { label: 'hanwha_dvr.name', key: 'name', sortable: false },
    { label: 'unit.description', key: 'unit.description', sortable: false },
    { label: 'unit.rems', key: 'unit.rems', sortable: false },
    { label: 'unit.sirh', key: 'unit.sirh', sortable: false },
    { label: 'unit.center', key: 'unit.center.name', sortable: false },
    { label: 'unit.sector', key: 'unit.sector.name', sortable: false },
    { label: "region.title", key: "unit.region.name", sortable: true },
    { label: "municipality.title", key: "unit.municipality.name", sortable: true },
    { label: 'hanwha_dvr.ip', key: 'ip', sortable: false },
    { label: 'router.ip_vpn', key: 'camera_proxy_router.camera.ip_vpn', sortable: false },
    { label: 'hanwha_dvr.user', key: 'user', sortable: false },
    { label: 'hanwha_dvr.password', key: 'password', sortable: false },
    { label: 'hanwha_dvr.recording_days', key: 'recording_days', sortable: false },
    { label: 'tenants.title', key: 'tenant.name', sortable: false },
    { label: 'unit.title', key: 'unit.description', sortable: false },
    { label: 'hanwha_dvr.days_recording_audit', key: 'days_recording_audit', sortable: false },
    { label: 'hanwha_dvr.enabled_recording_audit', key: 'enabled_recording_audit', sortable: false },
    { label: 'hanwha_dvr.model', key: 'model', sortable: false },
    { label: 'hanwha_dvr.firmware_version', key: 'firmware_version', sortable: false },
    //{ label: 'hanwha_dvr.build_date', key: 'build_date', sortable: false },
    { label: 'hanwha_dvr.device_type', key: 'device_type', sortable: false },
    { label: 'hanwha_dvr.local_ip', key: 'local_ip', sortable: false },
    { label: 'hanwha_dvr.mac_address', key: 'networks', sortable: false },
    { label: 'hanwha_alarm_profile.title', key: 'alarm_profile.name', sortable: false },
    { label: 'date.created_at', key: 'created_at', sortable: true, formatter: 'formatDateAssigned' },
    { label: 'date.updated_at', key: 'updated_at', sortable: true, formatter: 'formatDateAssigned' },
    { label: 'hanwha_dvr.auditStartAt', key: 'audit_start_at' },
  ]

  if (ability.can('update', 'HanwhaDvr') == true || ability.can('delete', 'HanwhaDvr') == true) {
    tableColumns.splice(0, 0, { label: 'actions.name', key: 'actions' })
  }

  const perPage = ref(10)
  const totalList = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('id')
  const isSortDirDesc = ref(true)
  const action = ref('list')
  const customQueryParams = ref({})

  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalList.value,
    }
  })

  const fetchList = () => {
    return store.dispatch('hanwha_dvr/getAll', {
      q: searchQuery.value,
      max: perPage.value,
      'page[n]': currentPage.value,
      sortBy: sortBy.value,
      sortDesc: isSortDirDesc.value,
      tenant: tenantFilter.value,
      unit: unitFilter.value,
      id_hanwha_alarm_profile: alarmProfileFilter.value,
      action: action.value,
      ...facetedFilters.value,
      ...customQueryParams.value,
    })
      .then(response => {
        if (action.value === 'export') {
          action.value = 'list'
          return []
        } else {
          totalList.value = response.meta.total
          return response.data
        }
      })
      .catch(() => {
        action.value = 'list'
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error obteniendo la información',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        return []
      })
  }

  const formatDateAssigned = value => {
    const formattedDate = moment(value).format('DD/MM/YYYY HH:mm:ss')
    return formattedDate
  }

  const refetchData = () => {
    refListTable.value.refresh()
  }

  watch([currentPage, perPage,], () => {
    refetchData()
  })

  watch([searchQuery], debounce(() => {
    refetchData()
  }, 500))

  const filters = ref({});
  const filtersCount = useFilterCounter(filters);
  const buildFilters = () => {
    filters.value = {
      tenant: tenantFilter.value,
      unit: unitFilter.value,
      id_hanwha_alarm_profile: alarmProfileFilter.value,
      ...facetedFilters.value,
      ...customQueryParams.value,
    };
    refetchData();
  }
  const clearSearchFilters = () => {
    tenantFilter.value = null;
    unitFilter.value = null;
    alarmProfileFilter.value = null;
    facetedFilters.value = {};
    customQueryParams.value = {};
    buildFilters();
  }

  const setFacetedFilters = values => {
    facetedFilters.value = { ...values, advanced_search: true }
    refetchData()
  }

  store.dispatch("municipality/getAll");
  const municipalityOptions = computed(
    () => store.getters["municipality/getSelectedItems"]
  );
  
  store.dispatch("region/getAll");
  const regionOptions = computed(() => store.getters["region/getSelectedItems"]);

  return {
    tableColumns,
    perPage,
    currentPage,
    totalList,
    fetchList,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refListTable,
    formatDateAssigned,
    tenantOptions,
    tenantFilter,
    unitFilter,
    alarmProfileFilter,
    alarmProfileOptions,
    unitOptions,
    centerOptions,
    sectorOptions,
    resellerOptions,
    dvrOptions,
    refetchData,
    filtersCount,
    buildFilters,
    clearSearchFilters,
    action,
    setFacetedFilters,
    customQueryParams,
    municipalityOptions,
    regionOptions,
  }
}
